import { Link } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { testimonials } from "@/mocks/kuaibao";

const principles = [
  {
    icon: "ri-speed-line",
    title: "快，但不慌",
    desc: "热点出现后第一时间整理，却不会为了抢速度而牺牲核对。宁可晚十分钟，也不发没把握的信息。",
  },
  {
    icon: "ri-filter-3-line",
    title: "筛选优先于堆量",
    desc: "我们不追求条数最多，而是把当天真正值得关注的十条挑出来。看完整页，比刷十个平台更有用。",
  },
  {
    icon: "ri-emotion-line",
    title: "克制地讨论",
    desc: "涉及个人隐私与未证实的传闻，我们只陈述已有信息，不渲染情绪，也不帮着放大猜测。",
  },
  {
    icon: "ri-refresh-line",
    title: "错了就改",
    desc: "内容有误会及时修订并标注更新记录，让每一条快报都经得起回溯，而不是悄悄消失在列表里。",
  },
];

const milestones = [
  {
    year: "起步",
    title: "从「每天整理十条」开始",
    desc: "最初只是把每天看到的热点手动梳理成一份清单，发给身边的朋友，意外地收到了很多「省事」的评价。",
  },
  {
    year: "成型",
    title: "快报速览模式上线",
    desc: "把每条热点压缩成一句话摘要，配上热度排序，让「一分钟刷完」从一句口号变成了可以稳定做到的事。",
  },
  {
    year: "现在",
    title: "分类与订阅补齐",
    desc: "内容和分类越攒越多之后，我们补上了分类浏览、关键词搜索和每日邮件订阅，让每个人只看自己想看的。",
  },
];

export default function About() {
  const aboutLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 51.cg今日吃瓜单",
    inLanguage: "zh-CN",
    url: "https://hizmfjw.cn/about",
    mainEntity: {
      "@type": "Organization",
      name: "51.cg 今日吃瓜单",
      url: "https://hizmfjw.cn/",
      email: "hello@51cg.example",
      description:
        "以榜单排序与一句话速览为核心的热点速览站，帮助用户一分钟刷完全部热点。",
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hizmfjw.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://hizmfjw.cn/about" },
    ],
  };

  return (
    <>
      <SeoHead
        title="关于我们 - 51.cg今日吃瓜单的内容原则"
        description="了解51.cg今日吃瓜单的定位与内容原则：以榜单排序和一句话速览为核心，帮用户一分钟刷完全部热点。这里也分享我们的发展理念、用户口碑与联系反馈方式。"
        keywords="51.cg,51.cg今日吃瓜单,吃瓜快报,关于我们,内容原则"
        path="/about"
        jsonLd={[aboutLd, breadcrumbLd]}
      />
      <SiteNavbar />
      <main className="w-full pt-16 md:pt-20">
        <section className="w-full bg-primary-950 pt-8 md:pt-12 pb-12 md:pb-16">
          <div className="w-full px-4 md:px-8">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-accent-400"></span>
                  <span className="font-label text-xs tracking-[0.2em] text-accent-400">
                    ABOUT US
                  </span>
                </div>
                <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 leading-tight mb-5">
                  我们想让「跟上热点」这件事，
                  <br className="hidden md:block" />
                  变得轻松一点
                </h1>
                <p className="font-label text-sm md:text-base text-background-300 leading-relaxed max-w-2xl">
                  51.cg 今日吃瓜单是一个以榜单排序和一句话速览为核心的热点速览站。
                  我们把每天散落在各处的讨论整理成一份瓜单，让你不用来回切换平台，
                  打开一页就能知道今天发生了什么。
                </p>
              </div>
              <div className="lg:col-span-5 grid grid-cols-3 gap-4">
                {[
                  { value: "8", label: "内容板块" },
                  { value: "3 次", label: "每日更新" },
                  { value: "1 分钟", label: "平均速览" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-4 rounded-lg bg-background-50/5 border border-background-50/10"
                  >
                    <div className="font-heading font-bold text-2xl md:text-3xl text-accent-400 mb-1">
                      {stat.value}
                    </div>
                    <div className="font-label text-xs text-background-300">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-5">
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="w-6 h-[3px] rounded-full bg-primary-500"></span>
                  <span className="font-label text-xs tracking-[0.2em] text-primary-600">
                    POSITIONING
                  </span>
                </div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">
                  网站定位
                </h2>
                <div className="flex flex-col gap-4 font-label text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    我们服务的不是「研究舆论」的人，而是想在碎片时间里快速了解今天发生了什么的人。
                    因此内容始终围绕两个词展开：<strong className="text-foreground-950">排序</strong>与
                    <strong className="text-foreground-950">压缩</strong>。
                  </p>
                  <p>
                    排序，是把热度更高的内容放在前面，让你先看最值得看的；压缩，是把信息密度提上来，
                    一句话讲清一件事，不做无意义的铺垫。
                  </p>
                  <p>
                    我们不生产情绪，也不制造对立。快报的价值在于帮你看清事实，而不是替你做判断。
                  </p>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
                  <span className="font-label text-xs tracking-[0.2em] text-accent-700">
                    PRINCIPLES
                  </span>
                </div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">
                  内容原则
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {principles.map((p) => (
                    <div
                      key={p.title}
                      className="p-5 rounded-lg border border-background-200/80 bg-background-100"
                    >
                      <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 mb-3">
                        <i className={`${p.icon} text-lg leading-none`}></i>
                      </span>
                      <h3 className="font-heading font-semibold text-foreground-950 text-base mb-1.5">
                        {p.title}
                      </h3>
                      <p className="font-label text-sm text-foreground-600 leading-relaxed">
                        {p.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="mb-8 md:mb-10">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="w-6 h-[3px] rounded-full bg-secondary-500"></span>
                <span className="font-label text-xs tracking-[0.2em] text-secondary-700">
                  JOURNEY
                </span>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                发展理念
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {milestones.map((m, i) => (
                <div
                  key={m.title}
                  className="relative p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/80"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-500 text-accent-950 font-heading font-bold text-sm">
                      {i + 1}
                    </span>
                    <span className="font-label text-xs tracking-widest text-foreground-400">
                      {m.year}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 text-base md:text-lg mb-2">
                    {m.title}
                  </h3>
                  <p className="font-label text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="mb-8 md:mb-10">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="w-6 h-[3px] rounded-full bg-primary-500"></span>
                <span className="font-label text-xs tracking-[0.2em] text-primary-600">
                  VOICES
                </span>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                用户怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <figure
                  key={t.name}
                  className="p-5 md:p-6 rounded-lg border border-background-200/80 bg-background-100 flex flex-col"
                >
                  <i className="ri-double-quotes-l text-2xl text-primary-300 leading-none mb-3"></i>
                  <blockquote className="font-label text-sm text-foreground-700 leading-relaxed flex-1">
                    {t.text}
                  </blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-background-200 flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-800 font-heading font-semibold text-sm">
                      {t.name.slice(0, 1)}
                    </span>
                    <span className="flex flex-col">
                      <span className="font-label text-sm text-foreground-900">{t.name}</span>
                      <span className="font-label text-xs text-foreground-400">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 pb-14 md:pb-20">
          <div className="w-full px-4 md:px-8">
            <div className="rounded-lg border border-background-200/80 bg-secondary-50 p-6 md:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                    联系我们
                  </h2>
                  <p className="font-label text-sm md:text-base text-foreground-600 leading-relaxed mb-5 max-w-lg">
                    如果你发现快报内容有误、想投稿线索，或者对我们的内容原则有建议，
                    欢迎随时来信。每一条反馈我们都会认真看，平均一个工作日内回复。
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="mailto:hello@51cg.example"
                      rel="nofollow"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium transition-colors whitespace-nowrap"
                    >
                      <i className="ri-mail-send-line leading-none"></i>
                      hello@51cg.example
                    </a>
                    <Link
                      to="/kuaibao"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-secondary-300 text-secondary-900 hover:bg-secondary-100 font-label text-sm transition-colors whitespace-nowrap"
                    >
                      <i className="ri-list-check-2 leading-none"></i>
                      浏览全部快报
                    </Link>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: "ri-inbox-archive-line", label: "线索投稿", value: "欢迎提供第一手信息" },
                    { icon: "ri-error-warning-line", label: "内容纠错", value: "核实后及时修订标注" },
                    { icon: "ri-time-line", label: "更新时段", value: "每日早 / 中 / 晚三次" },
                    { icon: "ri-shield-check-line", label: "隐私保护", value: "邮箱仅用于回复沟通" },
                  ].map((c) => (
                    <div
                      key={c.label}
                      className="p-4 rounded-lg bg-background-50 border border-secondary-200"
                    >
                      <i className={`${c.icon} text-xl text-secondary-700 leading-none`}></i>
                      <div className="font-heading font-semibold text-foreground-950 text-sm mt-3">
                        {c.label}
                      </div>
                      <div className="font-label text-xs text-foreground-500 mt-1">{c.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}